import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

/**
 * Serviço central de exportação (Excel / CSV) para DataTables e listas do frontend.
 * Preferir este módulo em vez de duplicar exportExcel/saveAsExcelFile nas páginas.
 */

function sanitizeFileBase(nome) {
  return String(nome || 'relatorio').replace(/[^a-z0-9_-]/gi, '_');
}

function fileNameWithDate(nomeArquivo, ext) {
  const dia = new Date().toISOString().split('T')[0];
  return `${sanitizeFileBase(nomeArquivo)}_${dia}.${ext}`;
}

function toCamelCaseKey(key) {
  if (!key) return key;
  return key.charAt(0).toLowerCase() + key.slice(1);
}

function shouldSkipExportKey(key) {
  return !key || key.startsWith('_');
}

function flattenExportValue(value) {
  if (value == null) return '';
  if (typeof value !== 'object') return value;
  if (Array.isArray(value)) return JSON.stringify(value);
  if (value instanceof Date) return value.toISOString();
  const descricao = value.Descricao ?? value.descricao;
  if (descricao != null && typeof descricao !== 'object') return descricao;
  const codigo = value.Codigo ?? value.codigo;
  if (codigo != null && typeof codigo !== 'object') return codigo;
  return JSON.stringify(value);
}

function normalizeRowForExport(row) {
  if (!row || typeof row !== 'object') return { Valor: row };

  const merged = {};
  Object.keys(row).forEach((key) => {
    if (shouldSkipExportKey(key)) return;
    const normalizedKey = toCamelCaseKey(key);
    const value = flattenExportValue(row[key]);
    if (merged[normalizedKey] === undefined || merged[normalizedKey] === '') {
      merged[normalizedKey] = value;
    } else if (value !== '' && value != null) {
      merged[normalizedKey] = value;
    }
  });
  return merged;
}

/**
 * Normaliza linhas de DataTable / objetos mistos PascalCase/camelCase.
 * @param {Array} rows
 * @param {Array<{ field: string, header?: string, value?: (row)=>any }>} [columns]
 *        Se omitido, exporta todos os campos de cada linha (union de chaves).
 */
export function mapRowsForExport(rows, columns) {
  const list = Array.isArray(rows) ? rows : [];
  if (!columns || columns.length === 0) {
    const normalized = list.map(normalizeRowForExport);
    const keyOrder = [];
    const seen = new Set();
    normalized.forEach((row) => {
      Object.keys(row).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          keyOrder.push(key);
        }
      });
    });
    return normalized.map((row) => {
      const out = {};
      keyOrder.forEach((key) => {
        out[key] = row[key] ?? '';
      });
      return out;
    });
  }

  return list.map((row) => {
    const out = {};
    columns.forEach((col) => {
      const header = col.header || col.field;
      let val;
      if (typeof col.value === 'function') {
        val = col.value(row);
      } else {
        const f = col.field;
        val =
          row?.[f] ??
          row?.[f?.charAt?.(0)?.toUpperCase() + f?.slice?.(1)] ??
          row?.[String(f || '').toLowerCase()] ??
          '';
      }
      if (val == null) val = '';
      else if (typeof val === 'object') val = JSON.stringify(val);
      out[header] = val;
    });
    return out;
  });
}

/**
 * Exporta dados para Excel (.xlsx)
 * @param {Array} dados - Array de objetos
 * @param {String} nomeArquivo - Nome sem extensão
 * @param {Object} [opts]
 * @param {Array} [opts.columns] - Colunas { field, header, value? }
 * @param {String} [opts.sheetName]
 */
export const exportarParaExcel = (dados, nomeArquivo = 'relatorio', opts = {}) => {
  const rows = mapRowsForExport(dados, opts.columns);
  if (!rows || rows.length === 0) {
    throw new Error('Nenhum dado para exportar');
  }

  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, opts.sheetName || 'Dados');

  const colWidths = Object.keys(rows[0] || {}).map((key) => {
    const maxLen = rows.reduce((max, row) => {
      const len = String(row?.[key] ?? '').length;
      return len > max ? len : max;
    }, String(key).length + 2);
    return { wch: Math.min(Math.max(maxLen + 2, 12), 60) };
  });
  ws['!cols'] = colWidths;

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(
    new Blob([wbout], { type: 'application/octet-stream' }),
    fileNameWithDate(nomeArquivo, 'xlsx')
  );
};

/**
 * Exporta dados para CSV
 */
export const exportarParaCSV = (dados, nomeArquivo = 'relatorio', opts = {}) => {
  const rows = mapRowsForExport(dados, opts.columns);
  if (!rows || rows.length === 0) {
    throw new Error('Nenhum dado para exportar');
  }

  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  saveAs(
    new Blob([csv], { type: 'text/csv;charset=utf-8;' }),
    fileNameWithDate(nomeArquivo, 'csv')
  );
};

/**
 * Atalho para DataTables do Primereact: passa value + definição de colunas.
 * Exibe alert se vazio (em vez de throw), para uso direto em onClick.
 */
export function exportDataTableExcel({ data, columns, fileName, sheetName }) {
  try {
    exportarParaExcel(data || [], fileName || 'exportacao', { columns, sheetName });
    return true;
  } catch (e) {
    // eslint-disable-next-line no-alert
    window.alert(e?.message || 'Nenhum dado para exportar');
    return false;
  }
}

export default {
  exportarParaExcel,
  exportarParaCSV,
  mapRowsForExport,
  exportDataTableExcel,
};
